/**
 * DaireSidebar Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A sophisticated resizable sidebar component for Daire AI chat integration.
 * Features include:
 * - Responsive design with adaptive width constraints across all device sizes
 * - Smooth Framer Motion animations for open/close/minimize states
 * - Interactive resizing with mouse drag functionality
 * - Fullscreen detection and state management
 * - Document context integration for PDF and content analysis
 * - Minimize/maximize functionality with floating positioning
 * - Professional overlay system for proper modal behavior
 * - Smart width management with device-specific constraints
 * - Production-ready with comprehensive error handling
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the sidebar is currently open
 * @param {function} props.onClose - Callback when sidebar is closed
 * @param {boolean} props.isMinimized - External control for minimized state
 * @param {function} props.onMinimizeChange - Callback when minimize state changes
 * @param {string} props.documentTitle - Title of document for context-aware chat
 * @param {boolean} props.isDocumentContext - Whether operating in document analysis mode
 * @param {string} props.listItemId - SharePoint list item ID for document context
 * @param {string} props.url - Document URL for context integration
 * @returns {JSX.Element} Animated resizable sidebar with Daire chat integration
 */
import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DaireChat from './DaireChat';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import './DaireChat.css';
import './Daire.css';

/**
 * Calculates responsive width constraints based on current screen size
 * 
 * Provides adaptive minimum and maximum widths for optimal user experience
 * across mobile, tablet, and desktop devices.
 * 
 * @returns {Object} Object with min and max width values in pixels
 */
const getResponsiveWidths = () => {
    const screenWidth = window.innerWidth;
    if (screenWidth <= 768) {
        // Mobile: take most of the screen
        return { min: Math.min(320, screenWidth * 0.9), max: screenWidth * 0.95 };
    } else if (screenWidth <= 1024) {
        // Tablet: 40-70% of screen
        return { min: screenWidth * 0.4, max: screenWidth * 0.7 };
    } else {
        // Desktop: 25-50% of screen, with absolute limits
        return { min: Math.max(400, screenWidth * 0.25), max: Math.min(800, screenWidth * 0.5) };
    }
};

/**
 * DaireSidebar Component
 * 
 * Main sidebar component with advanced responsive behavior and animation system.
 * Integrates with DaireChat for AI-powered document analysis and general assistance.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls sidebar visibility
 * @param {function} props.onClose - Handler for sidebar close events
 * @param {boolean} props.isMinimized - External minimized state control
 * @param {function} props.onMinimizeChange - Handler for minimize state changes
 * @param {string} props.documentTitle - Document title for context-aware sessions
 * @param {boolean} props.isDocumentContext - Enables document analysis mode
 * @param {string} props.listItemId - SharePoint item identifier
 * @param {string} props.url - Document URL for analysis context
 * @returns {JSX.Element} Animated sidebar with Daire chat integration
 */
function DaireSidebar({ 
    isOpen, 
    onClose, 
    isMinimized: externalMinimized, 
    onMinimizeChange,
    documentTitle,
    isDocumentContext,
    listItemId,
    url
}) {
    const [width, setWidth] = useState(() => getResponsiveWidths().min);
    const [isDragging, setIsDragging] = useState(false);
    const [activeDocumentContext, setActiveDocumentContext] = useState(false);
    const [hasShownWelcome, setHasShownWelcome] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const documentContext = useMemo(() => {
        return {
            isDocumentContext: isDocumentContext,
            title: documentTitle || '',
            listItemId: listItemId ? Number(listItemId) : null,
            url: url
        };
    }, [isDocumentContext, documentTitle, listItemId, url]);

    useEffect(() => {
        if (isOpen) {
            setActiveDocumentContext(isDocumentContext);
            // Reset welcome state when opening sidebar
            setHasShownWelcome(false);
        } else {
            setActiveDocumentContext(false);
        }
    }, [isOpen, isDocumentContext]);

    // Handle window resize to adjust sidebar width
    useEffect(() => {
        const handleResize = () => {
            const { min, max } = getResponsiveWidths();
            setWidth(prevWidth => {
                // Keep current width within new bounds
                return Math.min(Math.max(prevWidth, min), max);
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Handle fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement
            );
            setIsFullscreen(isCurrentlyFullscreen);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    const handleMinimizeToggle = () => {
        onMinimizeChange(!externalMinimized);
    };

    const handleClose = () => {
        setActiveDocumentContext(false);
        onClose();
    };

    const sidebarVariants = {
        open: {
            x: 0,
            y: 0,
            width: width,
            height: '100vh',
            transform: 'none',
            transition: {
                type: 'spring',
                stiffness: 300,
                damping: 30
            }
        },
        closed: {
            x: '100%',
            transform: 'none',
            transition: {
                type: 'spring',
                stiffness: 300,
                damping: 30
            }
        },
        minimized: {
            x: window.innerWidth <= 768 ? 'calc(100vw - 60px)' : 'calc(100vw - 80px)',
            y: window.innerWidth <= 768 ? 'calc(100vh - 60px)' : 'calc(100vh - 80px)',
            width: window.innerWidth <= 768 ? '50px' : '70px',
            height: window.innerWidth <= 768 ? '50px' : '70px',
            transition: {
                duration: 0.3,
                ease: 'easeInOut'
            }
        }
    };

    return (
        <AnimatePresence>
            {(isOpen || externalMinimized) && (
                <>
                    {!externalMinimized && (
                        <div 
                            className="sidebar-overlay" 
                            onClick={onClose}
                        />
                    )}
                    
                    <motion.div
                        className={`daire-sidebar-v2 daire-sidebar ${externalMinimized ? 'minimized' : ''} 
                                 ${isDragging ? 'dragging' : ''}`}
                        initial="closed"
                        animate={externalMinimized ? "minimized" : "open"}
                        exit="closed"
                        variants={sidebarVariants}
                    >


                        {/* Floating close button */}
                        {!externalMinimized && (
                            <Tippy content="Close Daire">
                                <button 
                                    className="daire-floating-close-btn"
                                    onClick={handleClose}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </Tippy>
                        )}

                        <div className="daire-content">
                            {externalMinimized ? (
                                <div 
                                    className="minimized-icon"
                                    onClick={() => onMinimizeChange(false)}
                                >
                                    <div className="daire-icon" />
                                </div>
                            ) : (
                                <>
                                    <DaireChat 
                                        showQuickActions={!activeDocumentContext}
                                        documentContext={documentContext}
                                        onNavigateHome={handleClose}
                                        showWelcome={!activeDocumentContext}
                                        onWelcomeShown={() => setHasShownWelcome(true)}
                                        showDocumentSuggestions={hasShownWelcome}
                                        isSidebar={true}
                                        fullPage={isFullscreen}
                                    />
                                </>
                            )}
                        </div>

                        {!externalMinimized && (
                            <div
                                className="daire-resize-handle"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    setIsDragging(true);
                                    const startX = e.clientX;
                                    const startWidth = width;

                                    const handleMouseMove = (e) => {
                                        const deltaX = startX - e.clientX;
                                        const { min, max } = getResponsiveWidths();
                                        const newWidth = Math.min(Math.max(startWidth + deltaX, min), max);
                                        setWidth(newWidth);
                                    };

                                    const handleMouseUp = () => {
                                        setIsDragging(false);
                                        document.removeEventListener('mousemove', handleMouseMove);
                                        document.removeEventListener('mouseup', handleMouseUp);
                                    };

                                    document.addEventListener('mousemove', handleMouseMove);
                                    document.addEventListener('mouseup', handleMouseUp);
                                }}
                            />
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

export default DaireSidebar; 